import { CommonModule, Location, ViewportScroller } from '@angular/common';
import { Component, Input, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { NavigationComponent } from '../../components/navigation/navigation.component';
import { AssessmentsService } from '../../services/assessments.service';
import { BbtData, TwoHandsNumberDataSet, initDataBBT } from '../../services/model';
import { getDateIsoString, getDateIsoStringPlus1Minute } from '../../services/util.functions';
import { AssessmentBaseComponent } from '../assessment-base/assessment-base.component';
import { DynamicTableInputComponent } from '../dynamic-table-input/dynamic-table-input.component';

@Component({
  selector: 'app-bbt',
  standalone: true,
  imports: [
    DynamicTableInputComponent,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatButtonModule,
    MatCardModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatChipsModule,
    MatSelectModule,
    MatTableModule,
    RouterModule,
    MatTooltipModule,
    MatIconModule,
    NavigationComponent,
  ],
  templateUrl: './bbt.component.html',
  styleUrl: './bbt.component.scss',
})
export class BbtComponent extends AssessmentBaseComponent implements OnInit {
  location: Location = inject(Location);
  @Input() assessmentId: string | undefined;
  protected readonly anleitung =
    'Beim BBT werden Blöcke aus einer Box über eine Trennwand in eine andere Box bewegt.\n\n' +
    'Messung: Anzahl der Übertragungen über die Trennwand innerhalb von 60 Sekunden\n\n' +
    'Werden zwei Blöcke gegriffen und zusammen über die Trennwand bewegt, zählt dies als ein Block.\n' +
    'Wird ein Block über die Trennwand geworfen, ohne dass sich die Fingerspitzen über die Trennwand bewegen, wird dieser nicht gezählt.\n\n';
  private assessmentService: AssessmentsService = inject(AssessmentsService);

  private scroller: ViewportScroller = inject(ViewportScroller);
  private fb: FormBuilder = inject(FormBuilder);
  assessmentData: WritableSignal<BbtData | undefined> = signal<BbtData | undefined>(undefined);
  assessmentDataNew: BbtData = JSON.parse(JSON.stringify(initDataBBT));
  hasError = signal({
    bbt: false,
  });

  form: FormGroup = this.fb.group({
    assessmentTime: [getDateIsoStringPlus1Minute(), Validators.required],
    note: [''],
  });

  onBbtData(data: { data: Array<TwoHandsNumberDataSet>; note: string; hasError: boolean }) {
    this.assessmentDataNew.bbt.data = data.data;
    this.assessmentDataNew.bbt.note = data.note;
    this.hasError.update(current => ({
      ...current,
      bbt: data.hasError,
    }));
  }

  ngOnInit(): void {
    this.scroller.scrollToPosition([0, 0]);

    if (this.assessmentId) {
      this.assessmentService.getAssessment(this.assessmentId).then(data => {
        const bbtData: BbtData = data.data as BbtData;
        // add manual
        bbtData.bbt.manual = initDataBBT.bbt.manual;

        this.assessmentData.set(bbtData);
        const date = getDateIsoString(new Date(data.dateIso));
        this.form.controls['assessmentTime'].patchValue(date);

        let note = data.note;
        const hasNewNote = note && note.length > 0;
        if (!hasNewNote) {
          // try migration
          note = bbtData.bbt.note;
          bbtData.bbt.note = '';
        }
        this.form.controls['note'].patchValue(note);

        this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
      });
    } else {
      this.assessmentData.set(initDataBBT);
      this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
    }
  }
  onSave() {
    const data: BbtData = this.assessmentDataNew as BbtData;
    const valueLeft = data.bbt.data[0].valueLeft;
    const valueRight = data.bbt.data[0].valueRight;

    if (this.hasError().bbt || (valueLeft === null && valueRight === null)) {
      return;
    }

    if (this.assessmentId) {
      this.assessmentService
        .updateAssessment(this.assessmentId, {
          type: 'BBT',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
        })
        .then(res => {
          this.router.navigate(['../../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'BBT', dId: this.assessmentId },
          });
        });
    } else {
      this.assessmentService
        .createAssessment({
          type: 'BBT',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
        })
        .then((docRef: DocumentReference) => {
          this.router.navigate(['../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'BBT', dId: docRef.id },
          });
        });
    }
  }

  onNavigateBack() {
    if (this.assessmentId) {
      // this.router.navigate(['../../../reports/assessments'], { relativeTo: this.route });
      this.location.back();
    } else {
      // this.router.navigate(['../../reports/assessments'], { relativeTo: this.route });
      this.router.navigate(['../../assessments/new'], { relativeTo: this.route });
    }
  }
}
